<template>
  <b-overlay :show="tableLoading" rounded="sm">
    <b-card no-body class="mb-0">
      <template v-if="!noHead" #header>
        <div class="d-flex justify-content-between align-items-center">
          <h4>
            <template v-if="!noTitle">
              {{ $t(`${localizationSectionName}.list_title`) }}
            </template>
          </h4>
          <div class="d-flex">
            <slot name="extra-actions" />
            <slot name="actions">
              <b-btn
                v-if="add"
                variant="secondary"
                size="sm"
                @click="$eventHub.$emit(`${dialogEventName}`)"
              >
                <base-icon icon="plus" size="sm" />
                {{
                  $te(`${localizationSectionName}.add_title`)
                    ? $t(`${localizationSectionName}.add_title`)
                    : $t(`common.add`)
                }}
              </b-btn>
            </slot>
          </div>
        </div>
      </template>

      <crud-table
        :no-name-prop="noNameProp"
        :entity-name="entityName"
        :update="update"
        :remove="remove"
        :filterId="filterId"
        :filterDefinitions="filterDefinitions"
        :filterLoadReferencesFn="filterLoadReferencesFn"
        :pageSize="tablePageSize"
        :localSorting="tableLocalSorting"
        :orderBy="tableOrderBy"
        :isDescending="tableIsDescending"
        :sortNameCol="tableSortNameCol"
        :saveSorting="tableSaveSorting"
        :columns="tableColumns"
        :itemsFn="tableItemsFn"
        :loadDataFn="tableLoadDataFn"
        :requestParamsFn="tableRequestParamsFn"
        :selectMode="tableSelectMode"
        :actionsWidth="tableActionsWidth"
        :sortable="sortable"
        :deleteRequestParamsFn="entityDeleteRequestParamsFn"
        :use-loader="false"
        :hide-header="tableHideHeader"
        @update:selected="$emit('update:selected', $event)"
      >
        <template
          v-for="slot in Object.keys($scopedSlots)"
          :slot="slot"
          slot-scope="scope"
        >
          <slot :name="slot" v-bind="scope" />
        </template>
      </crud-table>

      <slot
        v-if="add || update"
        name="dialog"
        :dialogEventName="dialogEventName"
        :success="itemUpdated"
      >
        <crud-dialog
          :entity-name="entityName"
          :loco-section="locoSection"
          :trigger-event-name="dialogEventName"
          :modal-size="dialogModalSize"
          :request-params-fn="dialogRequestParamsFn || tableRequestParamsFn"
          :edit-model-fn="dialogEditModelFn"
          :save-model-fn="dialogSaveModelFn"
          :load-references-fn="dialogLoadReferencesFn"
          :no-name-prop="noNameProp"
          :on-submit-action="dialogOnSubmitAction"
          @success="itemUpdated"
        >
          <template v-slot:form="{ editedItem }">
            <slot name="form" :editedItem="editedItem" />
          </template>
          <template #formFields="slot">
            <slot name="formFields" v-bind="slot" />
          </template>
        </crud-dialog>
      </slot>
    </b-card>
  </b-overlay>
</template>

<script>
import { hashMixin, crudCommonMixin, filterMixin } from "@foys-tech/foys-utils";

import { BaseIcon } from "@foys-tech/foys-base-components";
import CrudDialog from "./CrudDialog.vue";
import CrudTable from "./CrudTable.vue";

export default {
  mixins: [crudCommonMixin, filterMixin, hashMixin],
  components: { BaseIcon, CrudDialog, CrudTable },
  props: {
    /**
     * Do not show card header
     */
    noHead: {
      type: Boolean,
      default: false,
    },
    /**
     * Do not show title in card header
     */
    noTitle: {
      type: Boolean,
      default: false,
    },

    /**
     * Enable `add` functionality - show `Add` button in card header and use CrudDialog to add new entities.
     */
    add: {
      type: Boolean,
      default: true,
    },
    /**
     * Enable `update` functionality - show pencil icon in the table for every row and use CrudDialog to edit entity.
     */
    update: {
      type: Boolean,
      default: true,
    },
    /**
     * Enable `delete` functionality - show trash icon in the table for every row.
     */
    remove: {
      type: Boolean,
      default: true,
    },

    /* Related to filter */

    /**
     *  `BaseFilter`. Is used to store filter presets and filter options in the local storage. Defaults to `entityName` property.
     */
    filterId: {
      type: String,
    },
    /**
     * `BaseFilter`. Filter definitions for `BaseFilter` component.
     */
    filterDefinitions: {
      type: Array,
    },
    /**
     * `BaseFilter`. Callback to load data needed for `BaseFilter` component.
     */
    filterLoadReferencesFn: null,

    /* Related to table */

    /**
     * `Table` - indicates table page size
     */
    tablePageSize: {
      type: Number,
    },
    /**
     * `Table` - indicates if sorting is on client side or backend side
     */
    tableLocalSorting: {
      type: Boolean,
      default: false,
    },
    /**
     * `Table` - indicates initial column to order by
     */
    tableOrderBy: {
      type: String,
    },
    /**
     * `Table` - indicates if table is sorted descending
     */
    tableIsDescending: {
      type: Boolean,
      default: false,
    },
    /**
     * `Table` - enable sorting on name column
     */
    tableSortNameCol: {
      type: Boolean,
      default: true,
    },
    /**
     * `Table` - indicates if table sort/paging options should be saved to url hash
     */
    tableSaveSorting: {
      type: Boolean,
      default: false,
    },
    /**
     * `Table` - table columns settings
     */
    tableColumns: {
      type: Array,
      default: function () {
        return [];
      },
    },
    /**
     * `Table` - function to process table items before rendering in table
     */
    tableItemsFn: {
      type: Function,
    },
    /**
     * `Table` - function to load table data
     */
    tableLoadDataFn: {
      type: Function,
    },
    /**
     * `Table` - function to mutate default table request object (or return new)
     */
    tableRequestParamsFn: {
      type: Function,
    },
    /**
     * `Table` - indicates if rows can be selected in the table. Selecting rows in table emits `selected` event.
     */
    tableSelectMode: {
      type: String,
    },
    /**
     * `Table` - specify actions column width
     */
    tableActionsWidth: {
      type: String,
      default: "60px",
    },
    /**
     * `Table` - enable rows ordering functionality
     * proxy
     */
    sortable: {
      type: Boolean,
      default: false,
    },

    tableHideHeader: {
      type: Boolean,
      default: false,
    },

    /* Related to dialog */

    /**
     * `Dialog` - modal size
     */
    dialogModalSize: {
      type: String,
    },
    /**
     * `Dialog` - function to mutate default table request object (or return new)
     */
    dialogRequestParamsFn: {
      type: Function,
    },
    /**
     * `Dialog` - function to build edit model
     */
    dialogEditModelFn: {
      type: Function,
    },
    /**
     * `Dialog` - function to build save model
     */
    dialogSaveModelFn: {
      type: Function,
    },
    /**
     * `Dialog` - function to load related data needed for dialog
     */
    dialogLoadReferencesFn: {
      type: Function,
    },
    /**
     * `Dialog` - function called after base submit action
     */
    dialogOnSubmitAction: {
      type: Function,
    },

    /**
     * Function to mutate default entity delete request object (or return new)
     */
    entityDeleteRequestParamsFn: {
      type: Function,
    },
  },
  computed: {
    /* Related to table */

    tableLoading() {
      return (
        this.$store.getters[`${this.ns}${this.entityName}sLoading`] ||
        this.$store.getters[`${this.ns}${this.entityName}PositionSaving`]
      );
    },

    /* Related to dialog */
    dialogEventName() {
      return `${this.eventEntityName}-dialog`;
    },
  },
  methods: {
    itemUpdated(result) {
      this.$eventHub.$emit(`${this.eventEntityName}-updated`, result);
      this.$eventHub.$emit(`${this.eventEntityName}-refresh`);
    },
  },
};
</script>
